/**
 * @typedef {{[key: string]: any,}} NalaWholesaleCustomerData
 * @typedef {{[key: string]: any}} CustomerFormData
 * @typedef {{ready: (callback: () => void) => void, execute: (siteKey: string, options: { action: string }) => Promise<string>}} Grecaptcha
 * @typedef {{[key: string]: any}} NalaCart
 * @typedef {{[key: string]: any}} Product
 * @typedef {{[key: string]: any, values: Array<{ [key: string]: any }> }} CustomOptionValue
 * @typedef {{ id: string, options: Array<Array<CustomOptionValue>> }} CustomOption
 * @typedef {{id: string, quantity: number, properties: object}} CartItem
 */

import { NalaBase } from "@theme/nala-base";

/**
 * NalaWholesale Base Class
 * Base class for all NalaWholesale custom elements providing common functionality
 * @class
 * @extends {NalaBase}
 */
class NalaWholesaleBase extends NalaBase {
  /**
   * Creates an instance of NalaWholesaleBase
   * @constructor
   */
  constructor() {
    super();

    /** @type {boolean} Flag indicating if form validation has errors */
    this.error = false;
  }
}

/**
 * NalaWholesale Customer Register Component
 * Handles customer registration form submission, validation, and reCAPTCHA integration
 * @class
 * @extends {NalaWholesaleBase}
 */
class NalaWholesaleCustomerRegister extends NalaWholesaleBase {
  /**
   * Creates an instance of NalaWholesaleCustomerRegister
   * @constructor
   */
  constructor() {
    super();

    /** @type {HTMLFormElement|null} The registration form element */
    this.form = this.querySelector(".nala-ws-customer-form__form");

    /** @type {string} Google reCAPTCHA site key */
    this.recaptchaKey = this.getAttribute("data-recaptcha-key") || "-";
  }

  /**
   * Called when the element is inserted into the DOM
   * Sets up form submission listener and processes URL parameters
   * @returns {void}
   */
  connectedCallback() {
    if (this.form instanceof HTMLFormElement) {
      this.form.addEventListener("submit", (event) =>
        this.nlHandleFormSubmit(event)
      );
    }
    this.nlAddUrlParams();
  }

  /**
   * Handles form submission event
   * Validates form data, executes reCAPTCHA if configured, and submits the form
   * @param {Event} event - The form submission event
   * @returns {Promise<void>}
   */
  async nlHandleFormSubmit(event) {
    // check if form is html form element
    const form = this.form;
    if (!(form instanceof HTMLFormElement)) return;

    // prevent default form submission
    event.preventDefault();

    // validate form
    this.nlValidateForm();

    if (!this.error) {
      /** @type {HTMLButtonElement|null} */
      const button = this.querySelector(".nala-ws-customer-form__button");
      if (button instanceof HTMLButtonElement) {
        /** @type {HTMLSpanElement|null} */
        const buttonText = button?.querySelector(
          ".nala-ws-customer-form__button-text"
        );
        /** @type {HTMLSpanElement|null} */
        const buttonSpinner = button?.querySelector(
          ".nala-ws-customer-form__button-spinner"
        );

        if (buttonText && buttonSpinner) {
          buttonText.classList.add("nala-hidden");
          buttonSpinner.classList.remove("nala-hidden");
        }
      }

      const token = await this.nlGetRecaptchaToken(this.recaptchaKey);
      if (token) {
        const googleRecaptchaInput = form.querySelector(
          "[name='google-recaptcha']"
        );
        if (googleRecaptchaInput instanceof HTMLInputElement) {
          googleRecaptchaInput.value = token;
        }
      }
      localStorage.setItem("redirect_registration", "/pages/registration-form");
      form.submit();
    }
  }

  /**
   * Validates all form fields
   * Checks required fields and password match, sets error state and displays error messages
   * @returns {void}
   */
  nlValidateForm() {
    if (!(this.form instanceof HTMLFormElement)) return;
    this.error = false;
    this.nlRequiredField(this.form, "first_name");
    this.nlRequiredField(this.form, "last_name");
    this.nlRequiredField(this.form, "company_name");
    this.nlRequiredField(this.form, "email");
    this.nlRequiredField(this.form, "password");
    this.nlRequiredField(this.form, "confirm_password");
    this.nlPasswordMatch(this.form, "password", "confirm_password");
  }

  /**
   * Processes URL parameters and displays error messages if present
   * Checks for "error" parameter in URL query string and displays it in the error message element
   * @returns {void}
   */
  nlAddUrlParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.has("error")) {
      const errorMessage = this.querySelector(
        ".nala-ws-customer-form__error-message"
      );
      if (errorMessage) {
        errorMessage.textContent = params.get("error");
      }
    }
  }
}

customElements.define(
  "nala-ws-customer-register",
  NalaWholesaleCustomerRegister
);

/**
 * NalaWholesale Customer Form Component
 * Manages multi-step customer registration form with dynamic step navigation,
 * form validation, province loading, and conditional field display
 * @class
 * @extends {NalaWholesaleBase}
 */
class NalaWholesaleCustomerForm extends NalaWholesaleBase {
  /**
   * Creates an instance of NalaWholesaleCustomerForm
   * @constructor
   */
  constructor() {
    super();

    /** @type {number} The currently active step number (1-5) */
    this.activeStep = 2;

    /** @type {boolean} Flag indicating if customer is approved */
    this.isApproved = false;

    /** @type {boolean} Flag indicating if form is currently loading/submitting */
    this.loading = false;

    /** @type {NalaWholesaleCustomerData} Customer data object containing registration information */
    this.customerData = {};

    /** @type {boolean} Flag indicating if VAT number field should be displayed */
    this.isTaxVat = false;

    /** @type {boolean} Flag indicating if studio country is domestic */
    this.isDomestic = false;

    /** @type {boolean} Flag indicating if resale certificate is required */
    this.resaleCertificateRequired = false;

    /** @type {boolean} Flag indicating if customer is approved */
    this.isApproved = false;

    /** @type {boolean} Flag indicating if customer can add VAT number */
    this.canAddVat = false;

    /** @type {string} The recaptcha key */
    this.recaptchaKey = "-";

    /** @type {boolean} Flag indicating if form has changed */
    this.formChanged = false;

    /** @type {HTMLDivElement|null} The error message element */
    this.errorElement = this.querySelector(
      ".nala-ws-customer-form__error-message"
    );
  }

  /**
   * Called when the element is inserted into the DOM
   * Initializes customer data, sets up event listeners, and displays the appropriate step
   * @returns {void}
   */
  connectedCallback() {
    const customerData = this.getAttribute("data-customer-data") || "";
    if (customerData) {
      try {
        /** @type {NalaWholesaleCustomerData} */
        this.customerData = JSON.parse(customerData);
        this.isTaxVat = (this.customerData.taxvat || "") !== "";
        this.nlIsDomesticChanged();
        this.nlResaleCertificateRequiredChanged();
        this.isApproved = this.nlIsApproved();
        this.canAddVat = this.nlCanAddVat();
        this.recaptchaKey = this.customerData.recaptcha_key || "-";
      } catch (error) {
        console.error("Error parsing customer data:", error);
      }
    }
    this.nlInitializeEvents();
    this.nlAddUrlParams();
    this.nlInitValues();
    this.nlShowNextStep();
  }

  /**
   * Initializes all event listeners for the form
   * Sets up country change handler, step button listeners, and VAT number listeners
   * @returns {void}
   */
  nlInitializeEvents() {
    this.nlInitializeStudioCountry();
    this.nlAddStepButtonListeners();
    this.nlIsTaxVatListeners();
    this.nlFormChangedListeners();
  }

  /**
   * Checks if the studio country is domestic (United States)
   * @returns {boolean} True if studio country is United States, false otherwise
   */
  nlIsDomestic() {
    return (this.customerData.studio_country || "") === "United States";
  }

  /**
   * Checks if the studio country has changed
   * @returns {void}
   */
  nlIsDomesticChanged() {
    this.isDomestic = this.nlIsDomestic();
    this.nlUpdateTaxExempt();
  }

  /**
   * Checks if a resale certificate is required based on the studio state
   * Resale certificates are not required for Alaska, Delaware, Montana, Oregon, and New Hampshire
   * @returns {boolean} True if resale certificate is required, false otherwise
   */
  nlResaleCertificateRequired() {
    const states = ["Alaska", "Delaware", "Montana", "Oregon", "New Hampshire"];
    return !states.includes(this.customerData.studio_state || "");
  }

  /**
   * Updates the resale certificate required status
   * @returns {void}
   */
  nlResaleCertificateRequiredChanged() {
    this.resaleCertificateRequired = this.nlResaleCertificateRequired();
  }

  /**
   * Checks if the customer account is approved
   * @returns {boolean} True if customer status is "Approved", false otherwise
   */
  nlIsApproved() {
    return (this.customerData.is_approved || "") === "Approved";
  }

  /**
   * Checks if the customer can add a VAT number
   * @returns {boolean} True if customer is allowed to add VAT number, false otherwise
   */
  nlCanAddVat() {
    return (this.customerData.add_vat || "") === "1";
  }

  /**
   * Processes URL parameters to set the active step
   * Reads "step" parameter from URL query string and sets it as the active step
   * @returns {void}
   */
  nlAddUrlParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.has("step")) {
      this.activeStep = Number(params.get("step") || "2");
    }
  }

  /**
   * Displays the appropriate step based on activeStep and customer data
   * Hides all steps, shows the correct step based on activeStep and conditions,
   * updates step indicator active states, and scrolls to top of page
   * @returns {void}
   */
  nlShowNextStep() {
    document
      .querySelectorAll(".nala-ws-customer-form__step")
      .forEach((step) => {
        step.classList.add("nala-hidden");
        const stepData = step.getAttribute("data-step");
        if (this.activeStep === 1 && stepData === "personal") {
          step.classList.remove("nala-hidden");
        } else if (this.activeStep === 2 && stepData === "studio") {
          step.classList.remove("nala-hidden");
        } else if (this.activeStep === 3) {
          if (this.isDomestic) {
            if (this.resaleCertificateRequired) {
              if (stepData === "resale") {
                step.classList.remove("nala-hidden");
              }
            } else {
              if (stepData === "business-license") {
                step.classList.remove("nala-hidden");
              }
            }
          } else if (stepData === "social") {
            step.classList.remove("nala-hidden");
          }
        } else if (this.activeStep === 4) {
          if (this.isApproved) {
            if (stepData === "view") {
              step.classList.remove("nala-hidden");
            }
          } else {
            if (stepData === "final") {
              step.classList.remove("nala-hidden");
            }
          }
        } else if (this.activeStep > 4 && stepData === "view") {
          step.classList.remove("nala-hidden");
        }
      });

    // update steps active state
    const steps = this.querySelectorAll(".l-steps li");
    steps.forEach((step, index) => {
      if (
        index === this.activeStep - 1 ||
        (index === 3 && this.activeStep >= 4)
      ) {
        step.classList.add("is-active");
      } else {
        step.classList.remove("is-active");
      }
    });

    // scroll to top
    window.scrollTo(0, 0);
  }

  /**
   * Adds click event listeners to all step navigation buttons
   * @returns {void}
   */
  nlAddStepButtonListeners() {
    const buttons = this.querySelectorAll(".nala-ws-customer-form__button");
    buttons.forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.addEventListener("click", () =>
          this.nlHandleStepButtonClick(button)
        );
      }
    });
  }

  /**
   * Handles step navigation button clicks
   * Processes next, back, and edit actions, manages loading states, saves form data, and updates active step
   * @param {HTMLButtonElement} button - The button element that was clicked
   * @returns {Promise<void>}
   */
  async nlHandleStepButtonClick(button) {
    // get the step and action
    const step = button.getAttribute("data-step");
    const action = button.getAttribute("data-action");
    if (this.errorElement) {
      this.errorElement.textContent = "";
    }

    // handle view edit
    if (step === "view" && action === "edit") {
      this.activeStep = 2;
    } else if (action === "next") {
      // check if loading already exists
      if (this.loading) {
        return;
      }
      this.error = false;

      // get the button text and spinner
      const buttonText = button.querySelector(
        ".nala-ws-customer-form__button-text"
      );
      const buttonSpinner = button.querySelector(
        ".nala-ws-customer-form__button-spinner"
      );

      try {
        // hide the button text and spinner
        this.loading = true;
        if (buttonText && buttonSpinner) {
          buttonText.classList.add("nala-hidden");
          buttonSpinner.classList.remove("nala-hidden");
        }

        // save the step
        await this.nlSaveStep(step);

        // update the active step
        if (!this.error) {
          this.activeStep = this.activeStep + 1;
        }
      } catch (error) {
        console.error("Error saving step:", error);
        if (this.errorElement) {
          this.errorElement.textContent = error.message;
        }
      }

      // show the button text and spinner
      this.loading = false;
      if (buttonText && buttonSpinner) {
        buttonText.classList.remove("nala-hidden");
        buttonSpinner.classList.add("nala-hidden");
      }
    } else if (action === "back") {
      this.activeStep = this.activeStep - 1;
    }

    // check if the active step is valid
    if (this.activeStep < 1) {
      this.activeStep = 1;
    } else if (this.activeStep > 5) {
      this.activeStep = 5;
    }

    // show the next step
    if (!this.error) {
      if (this.activeStep === 5 || (this.activeStep === 4 && this.isApproved)) {
        document.location.href = "?step=5";
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return;
      }
      this.nlShowNextStep();
    }
  }

  /**
   * Loads and populates province/state options based on selected country
   * Reads province data from the selected country option's dataset and populates the province select field
   * Hides the province field if no provinces are available for the selected country
   * @returns {void}
   */
  nlLoadProvinces() {
    /** @type {HTMLSelectElement|null} */
    const country = this.querySelector(
      ".nala-ws-customer-addresses__form .country"
    );
    if (!country) {
      return;
    }
    const selectedOption = country.options[country.selectedIndex];
    try {
      /** @type {Array<[string, string]>} */
      const provinces = JSON.parse(selectedOption?.dataset?.provinces || "[]");
      const provinceContainer = this.querySelector(
        ".nala-ws-customer-addresses__form .province-field"
      );
      /** @type {HTMLSelectElement|null} */
      const provinceSelect = this.querySelector(
        ".nala-ws-customer-addresses__form .province"
      );
      if (!provinceContainer || !provinceSelect) {
        return;
      }

      // add studio change event listener to the province select field
      provinceSelect.addEventListener("change", () => {
        this.customerData.studio_state = provinceSelect.value;
        this.nlResaleCertificateRequiredChanged();
      });

      // if no provinces are available, hide the province field and set the studio state to empty
      if (provinces.length === 0) {
        provinceContainer.classList.add("nala-hidden");
        provinceSelect.innerHTML = "";
        this.customerData.studio_state = "";
        this.nlResaleCertificateRequiredChanged();
        return;
      }

      // set the province select options
      const defaultOption = this.customerData.studio_state || "";
      provinceSelect.innerHTML = provinces
        .map(
          (province) =>
            `<option value="${province[0]}" ${province[0] === defaultOption ? "selected" : ""
            }>${province[1]}</option>`
        )
        .join("");
      provinceContainer.classList.remove("nala-hidden");
      this.customerData.studio_state = provinceSelect.value;
      this.nlResaleCertificateRequiredChanged();
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Gets the appropriate resale certificate form URL based on studio state
   * Returns state-specific resale certificate form links, with default fallback to uniform certificate
   * @returns {string} The URL to the resale certificate form for the current studio state
   */
  nlResalesCertificateForm() {
    const studioState = this.customerData.studio_state || "";
    let link =
      "http://www.mtc.gov/Resources/Uniform-Sales-Use-Tax-Exemption-Certificate";
    if (
      ["Indiana", "Oklahoma", "West Virginia", "Wyoming"].includes(studioState)
    ) {
      link = "https://www.streamlinedsalestax.org/Shared-Pages/exemptions";
    } else if (studioState === "Louisiana") {
      link = "https://www.revenue.louisiana.gov/SalesTax/ResaleCertificate";
    } else if (studioState === "Massachusetts") {
      link =
        "https://www.mass.gov/doc/form-st-4-sales-tax-resale-certificate/download";
    } else if (studioState === "Mississippi") {
      link =
        "https://static.globalindustrial.com/site30/pdf/MississippiResaleCert.pdf";
    } else if (studioState === "Nevada") {
      link = "https://tax.nv.gov/Forms/General_Purpose_Forms/";
    } else if (studioState === "New York") {
      link = "https://www.tax.ny.gov/pdf/current_forms/st/st120_fill_in.pdf";
    } else if (studioState === "Virginia") {
      link =
        "https://www.tax.virginia.gov/forms/search?search=st-10&year=All&category=5&type=All";
    }
    return link;
  }

  /**
   * Initializes all form values and conditional field visibility
   * Updates resale link, tax exempt visibility, VAT number fields based on customer data
   * @returns {void}
   */
  nlInitValues() {
    this.nlUpdateResaleLink();
    this.nlUpdateIsTaxVat();
    this.nlUpdateTaxVat();
  }

  /**
   * Updates the resale certificate link href attribute
   * Sets the link to the appropriate state-specific resale certificate form URL
   * @returns {void}
   */
  nlUpdateResaleLink() {
    /** @type {HTMLAnchorElement|null} */
    const resaleLink = this.querySelector(
      ".nala-ws-customer-form__resale-link"
    );
    if (resaleLink instanceof HTMLAnchorElement) {
      resaleLink.href = this.nlResalesCertificateForm();
    }
  }

  /**
   * Updates the visibility of the tax exempt field based on domestic status
   * Shows tax exempt field for domestic customers (United States), hides for international
   * @returns {void}
   */
  nlUpdateTaxExempt() {
    const taxExempt = this.querySelector("#taxexempt_container");
    if (!taxExempt) {
      return;
    }

    if (this.isDomestic) {
      taxExempt.classList.remove("nala-hidden");
    } else {
      taxExempt.classList.add("nala-hidden");
    }
  }

  /**
   * Updates the visibility of the "add VAT number" checkbox field
   * Shows the field if customer is allowed to add VAT number
   * @returns {void}
   */
  nlUpdateIsTaxVat() {
    const isTaxVat = this.querySelector(".istaxvat-field");
    if (!isTaxVat) {
      return;
    }
    if (this.canAddVat) {
      isTaxVat.classList.remove("nala-hidden");
    }
  }

  /**
   * Updates the visibility of the VAT number input field
   * Shows/hides the field based on customer permissions and checkbox state
   * @returns {void}
   */
  nlUpdateTaxVat() {
    const taxvat = this.querySelector(".taxvat-field");
    if (!taxvat) {
      return;
    }
    if (this.canAddVat) {
      taxvat.classList.remove("nala-hidden");
    } else {
      taxvat.classList.add("nala-hidden");
    }
    if (this.isTaxVat) {
      taxvat.classList.remove("nala-vs-hidden");
    } else {
      taxvat.classList.add("nala-vs-hidden");
    }
  }

  /**
   * Updates the visibility of the country select field
   * Shows/hides the field based on customer permissions and checkbox state
   * @returns {void}
   */
  nlInitializeStudioCountry() {
    /** @type {HTMLFormElement|null} */
    const form = this.querySelector(".nala-ws-customer-form__studio-form");
    if (!(form instanceof HTMLFormElement)) return;

    /** @type {HTMLSelectElement|null} */
    const country = form.querySelector(".country_input");
    if (!country) return;

    // update the country value
    country.value = this.customerData.studio_country || "";

    // add change event listener to the country select field
    country.addEventListener("change", () => {
      // update the customer data
      this.customerData.studio_country = country.value;

      //update the domestic status
      this.nlIsDomesticChanged();

      // update provinces and tax exempt fields
      this.nlLoadProvinces();
    });
    this.nlLoadProvinces();
  }

  /**
   * Adds change event listener to the "add VAT number" checkbox
   * Updates VAT number field visibility when checkbox state changes
   * @returns {void}
   */
  nlIsTaxVatListeners() {
    /** @type {HTMLInputElement|null} */
    const isTaxVat = this.querySelector(".istaxvat-field .field__input");
    if (isTaxVat instanceof HTMLInputElement) {
      isTaxVat.addEventListener("change", () => {
        this.isTaxVat = isTaxVat.checked;
        this.nlUpdateTaxVat();
      });
    }
  }

  /**
   * Saves form data for the specified step
   * Includes a delay to simulate form submission, then calls step-specific save methods
   * @param {string|null} step - The step identifier to save (e.g., "personal", "studio", "resale")
   * @returns {Promise<void>}
   */
  async nlSaveStep(step) {
    // save the step data
    if (step === "personal") {
      await this.nlSaveStepPersonal();
    } else if (step === "studio") {
      await this.nlSaveStepStudio();
    } else if (step === "resale") {
      await this.nlSaveStepResale();
    } else if (step === "business-license") {
      await this.nlSaveStepBusinessLicense();
    } else if (step === "social") {
      await this.nlSaveStepSocial();
    }

    // reset the form changed flag
    if (!this.error) {
      this.formChanged = false;
    }
  }

  /**
   * Saves personal information step data
   * Placeholder for personal step form submission logic
   * @returns {Promise<void>}
   */
  async nlSaveStepPersonal() {
    const form = this.querySelector(".nala-ws-customer-form__personal-form");
    if (!(form instanceof HTMLFormElement)) return;

    this.nlValidateFormPersonal(form);
    if (this.error) return;

    // if the form has not changed, return
    if (!this.formChanged) {
      return;
    }

    // get the recaptcha token
    const token = await this.nlGetRecaptchaToken(this.recaptchaKey);

    // post the form data to the step1 endpoint
    const formData = new FormData(form);
    formData.set("ajax", "1");
    formData.set("customer_id", this.customerData.customer_id);
    formData.set("hash", this.customerData.hash);
    formData.set("email", this.customerData.email);
    formData.set("google-recaptcha", token);
    formData.set(
      "newsletter",
      formData.get("newsletter") === "on" ? "true" : "false"
    );
    const response = await fetch("/apps/nala-customer/step1", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    // update the studio company name
    const companyName = formData.get("company_name")?.toString() || "";
    if (companyName) {
      this.updateStudioCompanyName(companyName);
    }
  }

  /**
   * Validates personal information step form data
   * @param {HTMLFormElement} form - The form element to validate
   * @returns {void}
   */
  nlValidateFormPersonal(form) {
    this.nlRequiredField(form, "first_name");
    this.nlRequiredField(form, "last_name");
    this.nlRequiredField(form, "company_name");
  }

  /**
   * Updates the studio company name in the customer data
   * @param {string} companyName - The new company name
   * @returns {void}
   */
  updateStudioCompanyName(companyName) {
    const form = this.querySelector(".nala-ws-customer-form__studio-form");
    if (!(form instanceof HTMLFormElement)) return;
    const companyNameInput = form.querySelector(".company_name_input");
    if (!(companyNameInput instanceof HTMLInputElement)) return;
    companyNameInput.value = companyName;
  }

  /**
   * Saves studio information step data
   * @returns {Promise<void>}
   */
  async nlSaveStepStudio() {
    const form = this.querySelector(".nala-ws-customer-form__studio-form");
    if (!(form instanceof HTMLFormElement)) return;
    this.nlValidateFormStudio(form);
    if (this.error) return;

    // if the form has not changed, return
    if (!this.formChanged) {
      return;
    }

    // post the form data to the step2 endpoint
    const formData = new FormData(form);
    formData.set("ajax", "1");
    formData.set("customer_id", this.customerData.customer_id);
    formData.set("hash", this.customerData.hash);
    formData.set("email", this.customerData.email);
    formData.set("istax", formData.get("is_taxvat") == "on" ? "1" : "");
    formData.set(
      "tax_exempt",
      formData.get("tax_exempt") == "on" ? "true" : "false"
    );

    const response = await fetch("/apps/nala-customer/step2", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }
  }

  /**
   * Validates studio information step form data
   * @param {HTMLFormElement} form - The form element to validate
   * @returns {void}
   */
  nlValidateFormStudio(form) {
    this.nlRequiredField(form, "company_name");
    this.nlRequiredField(form, "phone");
    this.nlRequiredField(form, "address1");
    this.nlRequiredField(form, "country");
    this.nlRequiredField(form, "state");
    this.nlRequiredField(form, "city");
    this.nlRequiredField(form, "zipcode");
  }

  /**
   * Saves resale information step data
   * @returns {Promise<void>}
   */
  async nlSaveStepResale() {
    const form = this.querySelector(".nala-ws-customer-form__resale-form");
    if (!(form instanceof HTMLFormElement)) return;
    this.nlValidateFormResale(form);
    if (this.error) return;

    // if the form has not changed, return
    if (!this.formChanged) {
      return;
    }

    // post the form data to the step3 endpoint
    const formData = new FormData(form);
    formData.set("ajax", "1");
    formData.set("customer_id", this.customerData.customer_id);
    formData.set("hash", this.customerData.hash);
    formData.set("email", this.customerData.email);

    const response = await fetch("/apps/nala-customer/step3", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }
  }

  /**
   * Validates resale information step form data
   * @param {HTMLFormElement} form - The form element to validate
   * @returns {void}
   */
  nlValidateFormResale(form) {
    if (!this.customerData.resale_certificate) {
      this.nlRequiredFileField(form, "resale_certificate");
    }
  }

  /**
   * Saves business license information step data
   * @returns {Promise<void>}
   */
  async nlSaveStepBusinessLicense() {
    const form = this.querySelector(
      ".nala-ws-customer-form__business-license-form"
    );
    if (!(form instanceof HTMLFormElement)) return;
    this.nlValidateFormBusinessLicense(form);

    if (this.error) return;

    // if the form has not changed, return
    if (!this.formChanged) {
      return;
    }

    // post the form data to the step4 endpoint
    const formData = new FormData(form);
    formData.set("ajax", "1");
    formData.set("customer_id", this.customerData.customer_id);
    formData.set("hash", this.customerData.hash);
    formData.set("email", this.customerData.email);

    const response = await fetch("/apps/nala-customer/step4", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }
  }

  /**
   * Validates business license information step form data
   * @param {HTMLFormElement} form - The form element to validate
   * @returns {void}
   */
  nlValidateFormBusinessLicense(form) {
    if (!this.customerData.business_license) {
      this.nlRequiredFileField(form, "business_license");
    }
  }

  /**
   * Saves social information step data
   * @returns {Promise<void>}
   */
  async nlSaveStepSocial() {
    const form = this.querySelector(".nala-ws-customer-form__social-form");
    if (!(form instanceof HTMLFormElement)) return;

    // if the form has not changed, return
    if (!this.formChanged) {
      return;
    }

    // post the form data to the step4 endpoint
    const formData = new FormData(form);
    formData.set("ajax", "1");
    formData.set("customer_id", this.customerData.customer_id);
    formData.set("hash", this.customerData.hash);
    formData.set("email", this.customerData.email);

    const response = await fetch("/apps/nala-customer/step4", {
      method: "POST",
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }
  }

  /**
   * Adds change event listener to form fields
   * @returns {void}
   */
  nlFormChangedListeners() {
    this.querySelectorAll("form").forEach((form) => {
      form.addEventListener("change", () => {
        this.formChanged = true;
      });
      form.addEventListener("input", () => {
        this.formChanged = true;
      });
    });
  }
}

customElements.define("nala-ws-customer-form", NalaWholesaleCustomerForm);

/**
 * NalaWholesale Add to Cart Component
 * Manages add to cart functionality for wholesale customers
 * @class
 * @extends {NalaWholesaleBase}
 */
class NalaWholesaleAddToCart extends NalaWholesaleBase {
  /**
   * Creates an instance of NalaWholesaleAddToCart
   * @constructor
   */
  constructor() {
    super();

    /** @type {boolean} Flag indicating if the add to cart button should be shown */
    this.showAddToCartButton = false;

    /** @type {boolean} Flag indicating if the add to cart is currently loading */
    this.addingToCart = false;

    /** @type {boolean} Flag indicating if the product is available */
    this.available = this.dataset.available === "true";

    /** @type {boolean} Flag indicating if the product is shown */
    this.show = this.dataset.show === "true";

    /** @type {string} The customer id */
    this.customerId = this.dataset.customerId || "";

    /** @type {string} The product id */
    this.productId = this.dataset.productId || "";

    /** @type {string} The add type */
    this.addType = this.dataset.addType || "";

    /** @type {string} The wishlist hash */
    this.wishlistHash = this.dataset.wishlistHash || "";

    /** @type {boolean} Flag indicating if the wishlist is enabled */
    this.wishlistEnabled = this.dataset.wishlistEnabled === "1";

    /** @type {boolean} Flag indicating if the UOM dropdown is enabled */
    this.uomDropdown = this.dataset.uomDropdown === "1";

    /** @type {boolean} Flag indicating if the UOM hide default is enabled */
    this.uomHideDefault = this.dataset.uomHideDefault === "1";

    /** @type {boolean} Flag indicating if the UOM is available */
    this.uomAvailable = false;

    /** @type {Array<{ [key: string]: any }>} The UOM options */
    this.uoms = [];

    /** @type {{[key: string]: { [key: string]: any }[]}} The tier prices */
    this.tierPrices = {};

    /** @type {CustomOption[]} The custom options */
    this.customOptions = [];

    /** @type {boolean} Flag indicating if the currency is enabled */
    this.useCurrency = this.dataset.useCurrency === "true";

    /** @type {string} The currency sign */
    this.currencySign = this.dataset.currencySign || "";

    /** @type {boolean} Flag indicating if the option bundle is enabled */
    this.optionBundle = this.dataset.optionBundle === "true";
  }

  /**
   * Called when the element is inserted into the DOM
   * Initializes event listeners
   * @returns {void}
   */
  connectedCallback() {
    this.nlInitializeEvents();
  }

  /**
   * Initializes event listeners
   * @returns {Promise<void>}
   */
  async nlInitializeEvents() {
    if (!this.show) return;
    this.showAddToCartButton = this.available;

    if (this.addType === "bulk") {
      this.nlBulkQuantityButtonListeners();
      this.nlBulkInputQuantityListeners();
      this.nlBulkFilterChangedListeners();
      await this.nlInitializeTierPrices();
      await this.nlBulkInitializeUom();
      this.nlBulkShowAddToCartButton();
      this.nlBulkInitializeCustomOptions();
      this.nlBulkInitializeWishlist();
    } else {
      this.nlSingleQuantityButtonListeners();
      this.nlSingleInputQuantityListeners();
      await this.nlInitializeTierPrices();
      await this.nlSingleInitializeUom();
      this.nlSingleShowAddToCartButton();
      this.nlSingleInitializeCustomOptions();
      this.nlSingleInitializeWishlist();
    }
  }

  /**
   * Adds click event listener to quantity buttons
   * @returns {void}
   */
  nlBulkQuantityButtonListeners() {
    this.querySelectorAll(
      ".nala-ws-addtocart__bulk-variant-row-qty-button"
    ).forEach((button) => {
      button.addEventListener("click", () => {
        //@ts-ignore
        this.nlBulkUpdateQuantity(button);
      });
    });
  }

  /**
   * Adds click event listener to quantity buttons
   * @returns {void}
   */
  nlSingleQuantityButtonListeners() {
    this.querySelectorAll(
      ".nala-ws-addtocart__single-quantity-button"
    ).forEach((button) => {
      button.addEventListener("click", () => {
        //@ts-ignore
        this.nlSingleUpdateQuantity(button);
      });
    });
  }

  /**
   * Validates the quantity button
   * @param {HTMLButtonElement} button - The button element to validate
   * @returns {void}
   */
  nlBulkUpdateQuantity(button) {
    const increment = button.classList.contains("qty-plus");
    const input = button.parentElement?.querySelector(
      ".nala-ws-addtocart__quantity-input"
    );
    if (!(input instanceof HTMLInputElement)) return;

    this.nlUpdateQuantity(input, increment);
  }

  /**
   * Updates the quantity input field
   * @param {HTMLButtonElement} button - The button element to update the quantity
   * @returns {void}
   */
  nlSingleUpdateQuantity(button) {
    const increment = button.classList.contains("qty-plus");
    const input = button.parentElement?.querySelector(
      ".nala-ws-addtocart__quantity-input"
    );
    if (!(input instanceof HTMLInputElement)) return;

    this.nlUpdateQuantity(input, increment, true);
  }

  /**
   * Updates the quantity input field
   * @param {HTMLInputElement} input - The input element to update the quantity
   * @param {boolean} increment - Whether to increment the quantity
   * @param {boolean} defaultQty - The default quantity
   * @returns {void}
   */
  nlUpdateQuantity(input, increment, defaultQty = false) {
    const qtyUpdate = Number(input.dataset.qtyUpdate || "1");

    // get the quantity
    let qty = Number(input.value);
    if (isNaN(qty)) {
      qty = defaultQty ? qtyUpdate : 0;
    }

    // increment or decrement the quantity
    if (increment) {
      qty += qtyUpdate;
    } else {
      qty -= qtyUpdate;
    }

    // set the quantity
    if (qty > 0) {
      input.value = qty.toString();
    } else {
      input.value = defaultQty ? qtyUpdate.toString() : "0";
    }
  }

  /**
   * Adds input event listener to quantity input fields
   * @returns {void}
   */
  nlBulkInputQuantityListeners() {
    this.querySelectorAll(
      ".nala-ws-addtocart__bulk-variant-row-qty-input"
    ).forEach((input) => {
      input.addEventListener("change", () => {
        //@ts-ignore
        this.nlValidateQuantity(input);
      });
    });
  }

  /**
   * Adds input event listener to quantity input fields
   * @returns {void}
   */
  nlSingleInputQuantityListeners() {
    this.querySelector(".nala-ws-addtocart__single-quantity-input")?.addEventListener("change", () => {
      //@ts-ignore
      this.nlValidateQuantity(input, true);
    });
  }

  /**
   * Validates the quantity input field
   * @param {HTMLInputElement} input - The input element to validate
   * @param {boolean} defaultQty - The default quantity
   * @returns {void}
   */
  nlValidateQuantity(input, defaultQty = false) {
    const qtyUpdate = Number(input.dataset.qtyUpdate || "1");
    const qty = Number(input.value);
    if (isNaN(qty)) {
      input.value = defaultQty ? qtyUpdate.toString() : "0";
      return;
    }

    // check if the value is a number or less than 0
    if (qty % qtyUpdate !== 0) {
      const roundedQty = qty - (qty % qtyUpdate);
      input.value = defaultQty && roundedQty === 0 ? qtyUpdate.toString() : roundedQty.toString();
    } else if (defaultQty && qty === 0) {
      input.value = qtyUpdate.toString();
    }
  }

  /**
   * Shows the add to cart button if the variant total is greater than 0
   * @returns {void}
   */
  nlBulkShowAddToCartButton() {
    if (!this.available) return;

    this.querySelector(".nala-ws-addtocart__bulk-action")?.classList?.toggle("nala-hidden", !this.showAddToCartButton);
    this.nlBulkAddToCartButtonListeners();
  }

  /**
   * Shows the add to cart button if the product is available
   * @returns {void}
   */
  nlSingleShowAddToCartButton() {
    if (!this.available) return;

    this.querySelector(".nala-ws-addtocart__single-action")?.classList?.toggle("nala-hidden", !this.showAddToCartButton);
    this.nlSingleAddToCartButtonListeners();
  }

  /**
   * Adds click event listener to the add to cart button
   * @returns {void}
   */
  nlBulkAddToCartButtonListeners() {
    this.querySelector(
      ".nala-ws-addtocart__addtocart"
    )?.addEventListener("click", () => {
      this.nlBulkAddToCart();
    });
  }

  /**
   * Adds click event listener to the add to cart button
   * @returns {void}
   */
  nlSingleAddToCartButtonListeners() {
    this.querySelector(
      ".nala-ws-addtocart__addtocart"
    )?.addEventListener("click", () => {
      this.nlSingleAddToCart();
    });
  }

  /**
   * Adds the selected variants to the cart
   * @returns {Promise<void>}
   */
  async nlBulkAddToCart() {
    if (this.addingToCart) return;

    // get the add to cart button
    /** @type {HTMLElement | null} */
    const addToCartButton = this.querySelector(
      ".nala-ws-addtocart__addtocart"
    );
    if (!addToCartButton) return;

    // hide text and show spinner
    const { addToCartButtonText, addToCartButtonSpinner } = this.nlStartAddToCart(addToCartButton);

    // add to cart
    /** @type {CartItem[]} */
    let items = [];
    this.querySelectorAll(
      ".nala-ws-addtocart__bulk-variant-row-qty-input"
    ).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      // check if variant is not hidden
      /** @type {HTMLElement | null} */
      const variantRow = input.closest(".nala-ws-addtocart__bulk-variant-row");
      if (!variantRow || variantRow.classList.contains("nala-hidden")) return;

      // check if the quantity is a number and greater than 0
      const quantity = Number(input.value);
      if (isNaN(quantity) || quantity <= 0) return;

      // check if the variant id is set
      const variantId = variantRow.dataset.variantId;
      if (!variantId) return;

      // properties
      const properties = this.nlGetBulkItemProperties(variantRow, variantId);

      // add the item to the items array
      items.push({ id: variantId, quantity, properties });

      // reset the quantity input
      input.value = "0";
    });

    try {
      if (items.length === 0) {
        throw new Error("Please enter a valid quantity to add to cart");
      }
      await this.nlAddToCart(this.productId, items);

    } catch (error) {
      console.error("Error adding to cart", error);
      alert(error.message);
    }

    this.nlEndAddToCart(addToCartButtonText, addToCartButtonSpinner);
  }

  /**
   * Adds the selected variant to the cart
   * @returns {Promise<void>}
   */
  async nlSingleAddToCart() {
    if (this.addingToCart) return;

    /** @type {HTMLElement | null} */
    const variantRow = this.querySelector(`.nala-ws-addtocart__single`);
    if (!variantRow) return;

    const variantId = variantRow.dataset.variantId;
    if (!variantId) return;

    // get the add to cart button
    /** @type {HTMLElement | null} */
    const addToCartButton = variantRow.querySelector(
      ".nala-ws-addtocart__addtocart"
    );
    if (!addToCartButton) return;

    /** @type {HTMLInputElement | null} */
    const quantityInput = variantRow.querySelector(
      ".nala-ws-addtocart__single-quantity-input"
    );
    if (!quantityInput) return;
    const quantity = Number(quantityInput.value || "1");
    quantityInput.value = "1";

    // hide text and show spinner
    const { addToCartButtonText, addToCartButtonSpinner } = this.nlStartAddToCart(addToCartButton);


    try {
      const properties = this.nlGetSingleItemProperties(variantRow, variantId);
      const items = [{ id: variantId, quantity: quantity > 0 ? quantity : 1, properties }];
      this.nlAddToCart(this.productId, items);
    } catch (error) {
      console.error("Error adding to cart", error);
      alert(error.message);
    }

    this.nlEndAddToCart(addToCartButtonText, addToCartButtonSpinner);
  }

  /**
   * Hides the add to cart button text and shows the spinner
   * @param {HTMLElement} addToCartButton - The add to cart button element
   * @returns {{ addToCartButtonText: HTMLElement|null, addToCartButtonSpinner: HTMLElement|null }} The add to cart button text and spinner elements
   */
  nlStartAddToCart(addToCartButton) {
    this.addingToCart = true;

    // hide text and show spinner
    /** @type {HTMLElement | null} */
    const addToCartButtonText = addToCartButton.querySelector(
      ".nala-ws-addtocart__addtocart-text"
    );
    /** @type {HTMLElement | null} */
    const addToCartButtonSpinner = addToCartButton.querySelector(
      ".nala-ws-addtocart__addtocart-spinner"
    );
    if (addToCartButtonText && addToCartButtonSpinner) {
      addToCartButtonText.classList.add("nala-hidden");
      addToCartButtonSpinner.classList.remove("nala-hidden");
    }
    return { addToCartButtonText, addToCartButtonSpinner };
  }

  /**
   * Ends the add to cart process
   * @param {HTMLElement | null} addToCartButtonText - The add to cart button text element
   * @param {HTMLElement | null} addToCartButtonSpinner - The add to cart button spinner element
   * @returns {void}
   */
  nlEndAddToCart(addToCartButtonText, addToCartButtonSpinner) {
    this.addingToCart = false;

    // show text and hide spinner
    if (addToCartButtonText && addToCartButtonSpinner) {
      addToCartButtonText.classList.remove("nala-hidden");
      addToCartButtonSpinner.classList.add("nala-hidden");
    }
  }

  /**
   * Gets the bulk item properties
   * @param {HTMLElement} row - The variant element
   * @param {string} variantId - The variant id
   * @returns {{[key: string]: any}} The item properties
   */
  nlGetBulkItemProperties(row, variantId) {
    // get the UOM properties
    let uomQty = 0;
    let uomValue = "";
    if (this.uomAvailable) {
      /** @type {HTMLSelectElement | null} */
      const uomOption = row.querySelector(".nala-ws-addtocart__bulk-variant-uom-options-select");
      if (uomOption) {
        const [uom, qty] = uomOption.value.split("|");
        uomQty = Number(qty);
        // @ts-ignore
        uomValue = uom;
      }
    }

    // get tier discount
    const tierDiscount = this.nlGetTierDiscount(variantId, uomQty);

    // get the custom options for the variant
    const customOption = this.nlGetCustomOption(variantId);

    /** @type {{ [key: string]: string }} */
    const properties = {};
    /** @type {Array<{[key: string]: any}>} */
    let bundles = [];
    row.querySelectorAll(".nala-ws-addtocart__bulk-variant-option-input:checked").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      /** @type {HTMLInputElement | null} */
      const checkedOption = row.querySelector(`input[name="variant-custom-option-value-${variantId}-${input.value}"]:checked`);

      this.nlAddCustomOptionToProperties(properties, bundles, input, customOption, checkedOption, tierDiscount);
    });

    this.nlAddToCartProperties(properties, bundles, tierDiscount, uomQty, uomValue);
    return properties;
  }

  /**
   * Gets the single item properties
   * @param {HTMLElement} row - The variant element
   * @param {string} variantId - The variant id
   * @returns {{[key: string]: any}} The item properties
   */
  nlGetSingleItemProperties(row, variantId) {
    // get the UOM properties
    let uomQty = 0;
    let uomValue = "";
    if (this.uomAvailable) {
      /** @type {HTMLSelectElement | null} */
      const uomOption = row.querySelector(".nala-ws-addtocart__single-uom-option.checked");
      if (uomOption) {
        uomQty = Number(uomOption.dataset.qty || 0);
        uomValue = uomOption.dataset.uom || "";
      }
    }

    // get tier discount
    const tierDiscount = this.nlGetTierDiscount(variantId, uomQty);

    // get the custom options for the variant
    const customOption = this.nlGetCustomOption(variantId);

    /** @type {{ [key: string]: string }} */
    const properties = {};
    /** @type {Array<{[key: string]: any}>} */
    let bundles = [];
    row.querySelectorAll(".nala-ws-addtocart__single-custom-option-input:checked").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      /** @type {HTMLInputElement | null} */
      const checkedOption = row.querySelector(`input[name="variant-custom-option-value-${input.value}"]:checked`);

      this.nlAddCustomOptionToProperties(properties, bundles, input, customOption, checkedOption, tierDiscount);
    });

    this.nlAddToCartProperties(properties, bundles, tierDiscount, uomQty, uomValue);
    return properties;
  }

  /**
   * Adds the custom option to the properties
   * @param {{[key: string]: any}} properties - The properties
   * @param {Array<{[key: string]: any}>} bundles - The bundles
   * @param {HTMLInputElement} input - The input element
   * @param {CustomOption | undefined} customOption - The custom option
   * @param {HTMLInputElement | null} checkedOption - The checked option
   * @param {{[key: string]: any} | null} tierDiscount - The tier discount
   * @returns {void}
   */
  nlAddCustomOptionToProperties(properties, bundles, input, customOption, checkedOption, tierDiscount) {
    if (!customOption || !checkedOption) return;
    // @ts-ignore
    const optionObject = customOption.options.find((option) => option[0].name === input.value);
    // @ts-ignore
    const optionVariant = optionObject[0].values[0];
    if (this.optionBundle) {
      // @ts-ignore
      properties[optionObject[1].title] = checkedOption.value;
      bundles.push({
        // @ts-ignore
        id: optionVariant.variant_id,
        // @ts-ignore
        price: optionVariant.store_price,
        // @ts-ignore
        title: optionObject[1].title,
        // @ts-ignore
        value: checkedOption.value,
        disc: tierDiscount ? Number(tierDiscount.percent) : 0
      });
    } else {
      // @ts-ignore
      properties[optionObject[0].title] = optionVariant.value;
      // @ts-ignore
      properties[optionObject[1].title] = checkedOption.value;
      // @ts-ignore
      properties[`_nala_option_variant_id`] = optionVariant.variant_id;
      // @ts-ignore
      properties[`_nala_option_product_handle`] = optionVariant.product.handle;
    }
  }

  /**
   * Adds the properties to the properties
   * @param {{[key: string]: any}} properties - The properties
   * @param {Array<{[key: string]: any}>} bundles - The bundles
   * @param {{[key: string]: any} | null} tierDiscount - The tier discount
   * @param {number} uomQty - The UOM quantity
   * @param {string} uomValue - The UOM value
   * @returns {void}
   */
  nlAddToCartProperties(properties, bundles, tierDiscount, uomQty, uomValue) {
    if (bundles.length > 0) {
      properties['_nala_option_bundle'] = JSON.stringify(bundles);
    }

    if (tierDiscount) {
      properties['_nala_tier'] = 'yes'
    }

    if (this.uomAvailable && uomQty > 1) {
      properties['Pack Size'] = uomValue;
      properties['_nala_uom_qty'] = uomQty.toString();
    }
  }

  /**
   * Adds change event listener to filter input fields
   * @returns {void}
   */
  nlBulkFilterChangedListeners() {
    this.querySelectorAll(".nala-ws-addtocart__bulk-filter-select").forEach(
      (input) => {
        input.addEventListener("change", () => {
          this.nlBulkAddFilterToVariants();
        });
      }
    );
    this.querySelectorAll(".nala-ws-addtocart__bulk-filter-option").forEach(
      (input) => {
        input.addEventListener("change", () => {
          this.nlBulkAddFilterToVariants();
        });
      }
    );
  }

  /**
   * Adds the filter to the variants
   * @returns {void}
   */
  nlBulkAddFilterToVariants() {

    // get the filters
    /** @type {Array<Array<string>>} */
    let filters = [];
    this.querySelectorAll(".nala-ws-addtocart__bulk-filter").forEach((element) => {
      // check if the element is a select
      const isSelect = element.classList.contains("nala-filter-select");
      if (isSelect) {
        const select = element.querySelector(
          ".nala-ws-addtocart__bulk-filter-select"
        );

        // get the selected value
        if (select instanceof HTMLSelectElement && select.value) {
          filters.push([select.value]);
        } else {
          filters.push([]);
        }
      } else {
        // get the checked values
        /** @type {Array<string>} */
        let values = [];
        element.querySelectorAll(".nala-ws-addtocart__bulk-filter-option-input").forEach((checkbox) => {
          if (checkbox instanceof HTMLInputElement && checkbox.checked) {
            values.push(checkbox.value);
          }
        });
        filters.push(values);
      }
    });

    this.nlBulkShowOrHideVariants(filters);
  }

  /**
   * Shows or hides the variants
   * @param {Array<Array<string>>} filters - The filters
   * @returns {void}
   */
  nlBulkShowOrHideVariants(filters) {
    this.querySelectorAll(".nala-ws-addtocart__bulk-variant-row").forEach((variant) => {
      if (!(variant instanceof HTMLDivElement)) return;
      const option1 = variant.dataset.option1 || "";
      const option2 = variant.dataset.option2 || "";
      const option3 = variant.dataset.option3 || "";

      // check if the variant matches the filters
      let show = true;
      filters.forEach((filter, index) => {
        if (!show || filter.length === 0) return;
        if (index === 0 && !filter.includes(option1)) {
          show = false;
        } else if (index === 1 && !filter.includes(option2)) {
          show = false;
        } else if (index === 2 && !filter.includes(option3)) {
          show = false;
        }
      });

      // show or hide the variant
      variant.classList.toggle("nala-hidden", !show);
    });
  }

  /**
   * Initializes the wishlist
   * @returns {Promise<void>}
   */
  async nlBulkInitializeWishlist() {
    if (!this.wishlistEnabled) return;

    // get the wishlist
    const wishlists = await this.nlGetEnabledWishlists();

    this.querySelectorAll(".nala-ws-addtocart__bulk-variant-wishlist").forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      /** @type {HTMLElement | null} */
      const variantRow = element.closest(".nala-ws-addtocart__bulk-variant-row");
      if (!variantRow) return;

      const variantId = variantRow.dataset.variantId || "";
      if (!variantId) return;

      // show the wishlist element
      element.classList.remove("nala-hidden");

      // @ts-ignore
      if (Object.hasOwn(wishlists, variantId)) {
        this.nlUpdateWishlistIcon(element, true);
      } else {
        this.nlUpdateWishlistIcon(element, false);
      }

      this.nlAddWishlistListeners(element, variantId, "nala-ws-addtocart__bulk-variant-wishlist");
    });
  }

  /**
   * Initializes the wishlist
   * @returns {Promise<void>}
   */
  async nlSingleInitializeWishlist() {
    if (!this.wishlistEnabled) return;

    // get the wishlist
    const wishlists = await this.nlGetEnabledWishlists();

    // get the product element
    const productElement = this.closest(".product-information");
    if (!productElement) return;

    // get the wishlist element
    /** @type {HTMLElement | null} */
    const wishlistElement = productElement.querySelector(".nala-product-wishlist");
    if (!wishlistElement) return;

    /** @type {HTMLElement | null} */
    const variantRow = this.querySelector(".nala-ws-addtocart__single");
    if (!variantRow) return;

    const variantId = variantRow.dataset.variantId || "";
    if (!variantId) return;

    // show the wishlist element
    wishlistElement.classList.remove("nala-hidden");

    // @ts-ignore
    if (Object.hasOwn(wishlists, variantId)) {
      this.nlUpdateWishlistIcon(wishlistElement, true);
    } else {
      this.nlUpdateWishlistIcon(wishlistElement, false);
    }

    this.nlAddWishlistListeners(wishlistElement, variantId, "nala-product-wishlist");
  }

  /**
   * Gets the enabled wishlists for the variants
   * @returns {Promise<{ [key: string]: boolean }>}
   */
  async nlGetEnabledWishlists() {
    try {
      const url = `/apps/nala-wishlist/variant/product/${this.customerId}/${this.wishlistHash}/${this.productId}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to get variant wishlists");
      }
      const data = await response.json();
      if (!data.error && !Array.isArray(data.wishlist)) {
        return data.wishlist;
      }
      return {};
    } catch (error) {
      console.error("Error getting variant wishlists", error);
      return {};
    }
  }

  /**
   * Updates the wishlist icon
   * @param {HTMLElement} wishlistElement - The wishlist element
   * @param {boolean} isAdded - Whether the variant is added to the wishlist
   * @returns {void}
   */
  nlUpdateWishlistIcon(wishlistElement, isAdded) {
    wishlistElement
      .querySelectorAll(".nala-wishlist-add")
      .forEach((element) => {
        element.classList.toggle("nala-hidden", isAdded);
      });
    wishlistElement
      .querySelectorAll(".nala-wishlist-view")
      .forEach((element) => {
        element.classList.toggle("nala-hidden", !isAdded);
      });
  }

  /**
   * Adds click event listener to the wishlist elements
   * @param {HTMLElement} element - The wishlist element
   * @param {string} variantId - The variant id
   * @param {string} className - The class name
   * @returns {void}
   */
  nlAddWishlistListeners(element, variantId, className) {

    const addIcon = element.querySelector(
      `.${className}-add-icon`
    );
    if (addIcon) {
      addIcon.addEventListener("click", () => {
        this.nlAddWishlist(element, variantId, className);
      });
    }

    const viewIcon = element.querySelector(
      `.${className}-view-icon`
    );
    if (viewIcon) {
      viewIcon.addEventListener("click", () => {
        this.nlRemoveWishlist(element, variantId, className);
      });
    }

    const addText = element.querySelector(
      `.${className}-add-text`
    );
    if (addText) {
      addText.addEventListener("click", () => {
        this.nlAddWishlist(element, variantId, className);
      });
    }
  }

  /**
   * Adds the wishlist to the variant
   * @param {HTMLElement} element - The wishlist element
   * @param {string} variantId - The variant id
   * @param {string} className - The class name
   * @returns {Promise<void>}
   */
  async nlAddWishlist(element, variantId, className) {
    // start loading spinner
    const spinner = element.querySelector(
      `.${className}-spinner`
    );
    const icon = element.querySelector(
      `.${className}-add-icon`
    );
    if (!spinner || !icon || !spinner.classList.contains("nala-hidden")) return;
    spinner.classList.remove("nala-hidden");
    icon.classList.add("nala-hidden");

    // send request to add the wishlist
    try {
      const url = `/apps/nala-wishlist/variant/${this.customerId}/${this.wishlistHash}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ variant_id: variantId }),
      });

      // check if the response is ok
      if (!response.ok) {
        throw new Error("Failed to add wishlist");
      }

      // get the data
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // update the wishlist icon
      spinner.classList.add("nala-hidden");
      this.nlUpdateWishlistIcon(element, true);
    } catch (error) {
      console.error("Failed to add wishlist", error);
      alert(error.message);
      spinner.classList.add("nala-hidden");
      icon.classList.remove("nala-hidden");
    }
  }

  /**
   * Removes the wishlist from the variant
   * @param {HTMLElement} element - The wishlist element
   * @param {string} variantId - The variant id
   * @param {string} className - The class name
   * @returns {Promise<void>}
   */
  async nlRemoveWishlist(element, variantId, className) {
    // start loading spinner
    const spinner = element.querySelector(
      `.${className}-spinner`
    );
    const icon = element.querySelector(
      `.${className}-view-icon`
    );
    if (!spinner || !icon || !spinner.classList.contains("nala-hidden")) return;
    spinner.classList.remove("nala-hidden");
    icon.classList.add("nala-hidden");

    // send request to add the wishlist
    const url = `/apps/nala-wishlist/variant/${this.customerId}/${this.wishlistHash}/${variantId}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ action: "delete" }),
    });

    // check if the response is ok
    try {
      if (!response.ok) {
        throw new Error("Failed to remove wishlist");
      }

      // get the data
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }

      // update the wishlist icon
      spinner.classList.add("nala-hidden");
      this.nlUpdateWishlistIcon(element, false);
    } catch (error) {
      console.error("Failed to remove wishlist", error);
      alert(error.message);
      spinner.classList.add("nala-hidden");
      icon.classList.remove("nala-hidden");
    }
  }

  /**
   * Initializes the UOM
   * @returns {Promise<void>}
   */
  async nlBulkInitializeUom() {
    // get the UOM options
    await this.nlSetUomOptions();
    if (!this.uomAvailable) return;

    // show the header price UOM
    const headerPrice = this.querySelector(
      ".nala-ws-addtocart__bulk-header-price"
    );
    const headerPriceUom = this.querySelector(
      ".nala-ws-addtocart__bulk-header-price-uom"
    );
    if (headerPrice && headerPriceUom) {
      headerPrice.classList.add("nala-hidden");
      headerPriceUom.classList.remove("nala-hidden");
    }

    // add uom options to the variants
    /** @type {NodeListOf<HTMLElement>} */
    this.querySelectorAll(
      ".nala-ws-addtocart__bulk-variant-uom-options"
    ).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      /** @type {HTMLElement | null} */
      const variantRow = element.closest(".nala-ws-addtocart__bulk-variant-row");
      if (!variantRow) return;

      const variantId = variantRow.dataset.variantId || "";
      if (!variantId) return;

      // get the UOM options
      const { uomOptions, uomExists } = this.nlGetUomOptions(variantId);

      if (uomExists) {
        this.nlBulkAddUomOptions(element, uomOptions);
        /* @ts-ignore */
        this.nlBulkUpdateUomPrice(variantRow, uomOptions[0], variantId);
        /* @ts-ignore */
        this.nlBulkUpdateUomQuantity(variantRow, uomOptions[0].qty, false);
        this.nlBulkAddUomOptionChangeListeners(variantRow, variantId);
      }
    });
  }

  /**
   * Initializes the UOM
   * @returns {Promise<void>}
   */
  async nlSingleInitializeUom() {
    // get the UOM options
    await this.nlSetUomOptions();
    if (!this.uomAvailable) return;

    /** @type {HTMLElement | null} */
    const variantRow = this.querySelector(".nala-ws-addtocart__single");
    if (!variantRow) return;

    const variantId = variantRow.dataset.variantId || "";
    if (!variantId) return;

    // get the UOM wrapper
    /** @type {HTMLElement | null} */
    const uomWrapper = variantRow.querySelector(".nala-ws-addtocart__uom-wrapper");
    if (!uomWrapper) return;

    // get the UOM options
    const { uomOptions } = this.nlGetUomOptions(variantId);

    this.nlSingleAddUomOptions(uomWrapper, uomOptions);
    /* @ts-ignore */
    this.nlSingleUpdateUomPrice(variantRow, uomOptions[0], variantId);
    /* @ts-ignore */
    this.nlSingleUpdateUomQuantity(variantRow, uomOptions[0].qty, false);
    this.nlSingleAddUomOptionChangeListeners(variantRow, variantId);
  }

  /**
   * Gets the UOM options
   * @returns {Promise<void>}
   */
  async nlSetUomOptions() {
    this.uomAvailable = false;
    this.uoms = [];
    try {
      const url = `/apps/nala-uom/product/${this.productId}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to get UOM options");
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      const uoms = data.tabs;
      if (uoms.length > 0) {
        this.uomAvailable = true;
        this.uoms = uoms;
      }
    } catch (error) {
      console.error("Failed to get UOM options", error);
    }
  }

  /**
   * Gets the UOM options
   * @param {string} variantId - The variant id
   * @returns {{ uomOptions: Array<{ [key: string]: any }>, uomExists: boolean }}
   */
  nlGetUomOptions(variantId) {
    // get the UOM options
    /** @type {Array<{ [key: string]: any }>} */
    let uomOptions = [];
    let uomExists = false;
    if (this.uomHideDefault === false) {
      uomOptions.push({
        uom: "default",
        qty: 1,
      });
    }
    this.uoms.forEach((uom) => {
      const filteredVariants = uom.variants.filter(
        /** @ts-ignore */
        (variant) => variant.id === variantId
      );
      if (filteredVariants.length > 0) {
        uomExists = true;
        uomOptions.push({
          uom: uom.uom,
          qty: uom.qty,
        });
      }
    });
    return {
      uomOptions,
      uomExists,
    };
  }

  /**
   * Adds the UOM options to the variant
   * @param {HTMLElement} element - The variant element
   * @param {Array<{ [key: string]: any }>} uomOptions - The UOM options
   * @returns {void}
   */
  nlBulkAddUomOptions(element, uomOptions) {
    const selectOptions = `
      <select name="variant-uom[]" class="nala-ws-addtocart__bulk-variant-uom-options-select">
        ${uomOptions.map(uom =>
      `<option value="${uom.uom}|${uom.qty}">${uom.qty} ${uom.qty === 1 ? 'unit' : 'units'}</option>`
    ).join('')}
      </select>
    `;
    element.innerHTML = `<span>Pack Size</span>${selectOptions}`;
    element.classList.remove("nala-hidden");
  }

  /**
   * Adds the UOM options to the variant
   * @param {HTMLElement} element - The variant element
   * @param {Array<{ [key: string]: any }>} uomOptions - The UOM options
   * @returns {void}
   */
  nlSingleAddUomOptions(element, uomOptions) {
    const html = `
    <h4 class="nala-ws-addtocart__single-uom-title">Pack Size</h4>
      <div class="nala-ws-addtocart__single-uom-options">
        ${uomOptions.map((uom, index) =>
      `<div class="nala-ws-addtocart__single-uom-option ${index === 0 ? 'checked' : ''}" data-uom="${uom.uom}" data-qty="${uom.qty}">${uom.qty} ${uom.qty === 1 ? 'Unit' : 'Units'}</div>`
    ).join('')}
      </div>
    `;
    element.innerHTML = html;
  }

  /**
   * Adds the UOM option change listeners
   * @param {HTMLElement} variantRow - The variant row element
   * @param {string} variantId - The variant id
   * @returns {void}
   */
  nlBulkAddUomOptionChangeListeners(variantRow, variantId) {
    /** @type {HTMLSelectElement | null} */
    const select = variantRow.querySelector(
      ".nala-ws-addtocart__bulk-variant-uom-options-select"
    );
    if (!select) return;

    select.addEventListener("change", (event) => {
      // @ts-ignore
      const value = event.target.value;
      const [uom, qty] = value.split("|");
      this.nlBulkUpdateUomPrice(variantRow, { uom, qty }, variantId);
      this.nlBulkUpdateUomQuantity(variantRow, qty, true);
    });
  }

  /**
   * Adds the UOM option change listeners
   * @param {HTMLElement} variantRow - The variant row element
   * @param {string} variantId - The variant id
   * @returns {void}
   */
  nlSingleAddUomOptionChangeListeners(variantRow, variantId) {
    variantRow.querySelectorAll(".nala-ws-addtocart__single-uom-option").forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      option.addEventListener("click", () => {

        // remove the checked class from all the options
        variantRow.querySelectorAll(".nala-ws-addtocart__single-uom-option").forEach((oldOption) => {
          oldOption.classList.remove("checked");
        });

        // add the checked class to the selected option
        option.classList.add("checked");
        const uom = option.dataset.uom || "";
        const qty = Number(option.dataset.qty || 0);
        this.nlSingleUpdateUomPrice(variantRow, { uom, qty }, variantId);
        this.nlSingleUpdateUomQuantity(variantRow, qty, true);
      });
    });
  }

  /**
   * Updates the UOM price
   * @param {HTMLElement} variantRow - The variant row element
   * @param {{ [key: string]: any }} uomOption - The UOM option
   * @param {string} variantId - The variant id
   * @returns {void}
   */
  nlBulkUpdateUomPrice(variantRow, uomOption, variantId) {

    const price = Number(variantRow.dataset.price || 0);
    const compareAtPrice = Number(variantRow.dataset.compareAtPrice || 0);
    if (price <= 0) return;

    const uomPriceElement = variantRow.querySelector(
      ".nala-ws-addtocart__bulk-variant-price-uom"
    );
    if (!uomPriceElement) return;

    let discountPrice = price;
    if (uomOption.uom !== "default") {
      discountPrice = this.nlGetTierDiscountPrice(variantId, price, uomOption.qty);
    }

    const priceHtml = discountPrice < price
      ? `<span class="nala-variant-price-wrapper">
          <span class="nala-variant-price">${this.nlFormatMoney(discountPrice, this.currencySign, false)}</span>
          <span class="nala-variant-actual-price">${this.nlFormatMoney(price, this.currencySign, false)}</span>
        </span>`
      : `<span class="nala-variant-price">${this.nlFormatMoney(price, this.currencySign, false)}</span>`;
    const html = `${priceHtml}<span class="nala-variant-price-uom-text">/Unit</span>`;
    uomPriceElement.innerHTML = html;

    variantRow
      .querySelectorAll(".nala-ws-addtocart__bulk-variant-price")
      .forEach((element) => {
        element.classList.add("nala-hidden");
      });
    uomPriceElement.classList.remove("nala-hidden");
  }

  /**
   * Updates the UOM price
   * @param {HTMLElement} variantRow - The variant row element
   * @param {{ [key: string]: any }} uomOption - The UOM option
   * @param {string} variantId - The variant id
   * @returns {void}
   */
  nlSingleUpdateUomPrice(variantRow, uomOption, variantId) {
    const price = Number(variantRow.dataset.price || 0);
    const compareAtPrice = Number(variantRow.dataset.compareAtPrice || 0);
    if (isNaN(price) || isNaN(compareAtPrice)) return;

    const productElement = variantRow.closest(".product-information");
    if (!productElement) return;

    const priceElement = productElement.querySelector("div[ref='priceContainer']");
    if (!priceElement) return;

    let discountPrice = price;
    if (uomOption.uom !== "default") {
      discountPrice = this.nlGetTierDiscountPrice(variantId, price, uomOption.qty);
    }

    const priceHtml = discountPrice < price
      ? `<span role="group">
          <span class="price price-range price--large">${this.nlFormatMoney(discountPrice, this.currencySign, false)}</span>
        </span>
        <span role="group">
          <span class="compare-at-price">${this.nlFormatMoney(price, this.currencySign, false)}</span>
        </span>`
      : `<span role="group">
          <span class="price price-range price--large">${this.nlFormatMoney(price, this.currencySign, false)}</span>
        </span>`;
    priceElement.innerHTML = priceHtml;
  }

  /**
   * Updates the UOM quantity
   * @param {HTMLElement} variantRow - The variant row element
   * @param {number} qty - The quantity
   * @param {boolean} isUpdate - Whether the quantity is being updated
   * @returns {void}
   */
  nlBulkUpdateUomQuantity(variantRow, qty, isUpdate) {

    /** @type {HTMLInputElement | null} */
    const input = variantRow.querySelector(
      ".nala-ws-addtocart__bulk-variant-row-qty-input"
    );
    if (!input) return;
    this.nlUpdateQtyIncrementer(input, qty, isUpdate);
  }

  /**
   * Updates the UOM quantity
   * @param {HTMLElement} variantRow - The variant row element
   * @param {number} qty - The quantity
   * @param {boolean} isUpdate - Whether the quantity is being updated
   * @returns {void}
   */
  nlSingleUpdateUomQuantity(variantRow, qty, isUpdate) {
    /** @type {HTMLInputElement | null} */
    const input = variantRow.querySelector(
      ".nala-ws-addtocart__single-quantity-input"
    );
    if (!input) return;
    this.nlUpdateQtyIncrementer(input, qty, isUpdate);
  }

  /**
   * Updates the quantity incrementer
   * @param {HTMLInputElement} input - The input element to update the quantity
   * @param {number} qty - The quantity
   * @param {boolean} isUpdate - Whether the quantity is being updated
   * @returns {void}
   */
  nlUpdateQtyIncrementer(input, qty, isUpdate) {
    input.dataset.qtyUpdate = qty.toString();
    if (isUpdate) {
      input.value = qty.toString();
    }
  }

  /**
   * Initializes the tier prices
   * @returns {Promise<void>}
   */
  async nlInitializeTierPrices() {
    this.tierPrices = await this.nlGetTierPrices();
  }

  /**
   * Gets the tier prices
   * @returns {Promise<{ [key: string]: { [key: string]: any }[] }>}
   */
  async nlGetTierPrices() {
    try {
      const url = `/apps/nala-tier-price/product/${this.productId}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to get tier prices");
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      return data;
    } catch (error) {
      console.error("Failed to get tier prices", error);
      return {};
    }
  }

  /**
   * Gets the tier discount
   * @param {string} variantId - The variant id
   * @param {number} qty - The quantity
   * @returns {{ [key: string]: any } | null}
   */
  nlGetTierDiscount(variantId, qty) {
    const tierPrices = this.tierPrices[variantId];
    if (!tierPrices) return null;

    const currentTierPrice = tierPrices.find(
      (tierPrice) => Number(tierPrice.qty) === Number(qty)
    );
    return currentTierPrice || null;
  }

  /**
   * Gets the tier discount
   * @param {string} variantId - The variant id
   * @param {number} price - The price
   * @param {number} qty - The quantity
   * @returns {number}
   */
  nlGetTierDiscountPrice(variantId, price, qty) {
    const tierDiscount = this.nlGetTierDiscount(variantId, qty);
    if (!tierDiscount) return price;

    return (100 - Number(tierDiscount.percent)) * price / 100;
  }

  /**
   * Initializes the custom options
   * @returns {Promise<void>}
   */
  async nlBulkInitializeCustomOptions() {
    await this.nlSetCustomOptions();
    this.nlBulkAddVariantCustomOptions();
    this.nlBulkAddCustomOptionChangeListeners();
  }

  /**
   * Initializes the custom options
   * @returns {Promise<void>}
   */
  async nlSingleInitializeCustomOptions() {
    await this.nlSetCustomOptions();
    this.nlSingleAddCustomOptions();
    this.nlSingleAddCustomOptionChangeListeners();
  }

  /**
   * Gets the custom options
   * @returns {Promise<void>}
   */
  async nlSetCustomOptions() {
    this.customOptions = [];
    try {
      const url = `/apps/nala-product-option/variant/${this.productId}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to get custom options");
      }
      const data = await response.json();
      if (data.error) {
        throw new Error(data.error);
      }
      this.customOptions = data;
    } catch (error) {
      console.error("Failed to get custom options", error);
    }
  }

  /**
   * Gets the custom option
   * @param {string} variantId - The variant id
   * @returns {CustomOption | undefined} The custom option
   */
  nlGetCustomOption(variantId) {
    return this.customOptions.find((option) => option.id === variantId);
  }

  /**
   * Adds the custom options to the variant
   * @returns {void}
   */
  nlBulkAddVariantCustomOptions() {
    this.querySelectorAll(
      ".nala-ws-addtocart__bulk-variant-options"
    ).forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      /** @type {HTMLElement | null} */
      const variantRow = element.closest(".nala-ws-addtocart__bulk-variant-row");
      if (!variantRow) return;

      const variantId = variantRow.dataset.variantId || "";
      if (!variantId) return;

      const customOption = this.nlGetCustomOption(variantId);
      if (!customOption) return;

      this.nlBulkAddCustomOption(element, customOption.options, variantId);
      this.nlBulkAddCustomOptionValues(customOption.options, variantId);
    });
  }

  /**
   * Adds the custom options to the variant
   * @returns {void}
   */
  nlSingleAddCustomOptions() {
    /** @type {HTMLElement | null} */
    const customOptionsElement = this.querySelector(".nala-ws-addtocart__single-custom-options");
    if (!customOptionsElement) return;

    /** @type {HTMLElement | null} */
    const variantRow = this.querySelector(".nala-ws-addtocart__single");
    if (!variantRow) return;

    const variantId = variantRow.dataset.variantId || "";
    if (!variantId) return;

    const customOption = this.nlGetCustomOption(variantId);
    if (!customOption) return;

    this.nlSingleAddCustomOption(customOptionsElement, customOption.options);
  }

  /**
   * Adds the custom option to the variant
   * @param {HTMLElement} element - The variant element
   * @param {Array<{ [key: string]: any }>} options - The custom options
   * @param {string} variantId - The variant id
   * @returns {void}
   */
  nlBulkAddCustomOption(element, options, variantId) {
    const html = `
      ${options
        .map(option => {
          const { name, title, values } = option[0];
          const id = `custom-option-${variantId}-${name}`;
          // @ts-ignore
          const price = values.reduce((acc, value) => acc + Number(value.store_price) * 100, 0);
          return `
            <div class="nala-ws-addtocart__bulk-variant-option-item">
              <input type="checkbox" id="${id}" name="variant-custom-option[]" value="${name}" class="nala-ws-addtocart__bulk-variant-option-input" data-price="${price}">
              <label for="${id}">${title}</label>
            </div>
          `;
        })
        .join('')}
    `;
    element.innerHTML = html;
  }

  /**
   * Adds the custom option to the variant
   * @param {HTMLElement} element - The variant element
   * @param {Array<{ [key: string]: any }>} options - The custom options
   * @returns {void}
   */
  nlSingleAddCustomOption(element, options) {
    const html = `
      ${options
        .map(option => {
          const { name, title, values } = option[0];
          const id = `custom-option-${name}`;
          // @ts-ignore
          const price = values.reduce((acc, value) => acc + Number(value.store_price) * 100, 0);
          return `
            <div class="nala-ws-addtocart__single-custom-option-item">
              <div class="nala-ws-addtocart__single-custom-option-main">
                <input type="checkbox" id="${id}" name="variant-custom-option[]" value="${name}" class="nala-ws-addtocart__single-custom-option-input" data-price="${price}">
                <label for="${id}">${title}</label>
                <span class="nala-ws-addtocart__single-custom-option-price-${name}"></span>
              </div>
              ${this.nlSingleAddCustomOptionValues(option)}
            </div>
          `;
        })
        .join('')}
    `;
    element.innerHTML = html;
  }

  /**
   * Adds the custom option values to the variant
   * @param {Array<{ [key: string]: any }>} options - The custom options
   * @param {string} variantId - The variant id
   * @returns {void}
   */
  nlBulkAddCustomOptionValues(options, variantId) {
    const element = this.querySelector(`.nala-ws-addtocart__bulk-variant-option-cell-${variantId}`);
    if (!element) return;
    const html = options
      .map(option => {
        const { name: mainName } = option[0];
        /** @type {CustomOptionValue} */
        const { name: subName, values } = option[1];

        return `
          <div class="nala-ws-addtocart__option-value nala-ws-addtocart__bulk-variant-option-value nala-ws-addtocart__bulk-variant-option-value-${mainName} nala-hidden">
            ${values.map((value, index) => {
          const { value: subValue } = value;
          const id = `custom-option-value-${variantId}-${mainName}-${subName}-${this.nlHandleize(subValue)}`;
          return `
                <div class="nala-ws-addtocart__option-value-item nala-ws-addtocart__bulk-variant-option-value-item">
                  <input type="radio" id="${id}" name="variant-custom-option-value-${variantId}-${mainName}" value="${subValue}" ${index === 0 ? 'checked' : ''} class="nala-ws-addtocart__bulk-variant-option-value-radio">
                  <label for="${id}">${subValue}</label>
                </div>
              `;
        }).join('')}
          </div>
        `;
      })
      .join('');
    element.innerHTML = html;
  }

  /**
   * Adds the custom option values to the variant
   * @param {{ [key: string]: any }} option - The custom option
   * @returns {string}
   */
  nlSingleAddCustomOptionValues(option) {
    const { name: mainName } = option[0];
    /** @type {CustomOptionValue} */
    const { name: subName, values } = option[1];
    return `<div class="nala-ws-addtocart__option-value nala-ws-addtocart__single-custom-option-value nala-ws-addtocart__single-custom-option-value-${mainName} nala-hidden">
      ${values.map((value, index) => {
      const { value: subValue } = value;
      const id = `custom-option-value-${mainName}-${subName}-${this.nlHandleize(subValue)}`;
      return `
          <div class="nala-ws-addtocart__option-value-item nala-ws-addtocart__single-custom-option-value-item">
            <input type="radio" id="${id}" name="variant-custom-option-value-${mainName}" value="${subValue}" ${index === 0 ? 'checked' : ''} class="nala-ws-addtocart__single-custom-option-value-radio">
            <label for="${id}">${subValue}</label>
          </div>
        `;
    }).join('')
      }</div>`;
  }

  /**
   * Adds the custom option change listeners
   * @returns {void}
   */
  nlBulkAddCustomOptionChangeListeners() {
    this.querySelectorAll(".nala-ws-addtocart__bulk-variant-option-input").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.addEventListener("change", (event) => {
        /** @type {HTMLElement | null} */
        const variantRow = input.closest(".nala-ws-addtocart__bulk-variant-row");
        if (!variantRow) return;

        const variantId = variantRow.dataset.variantId || "";
        if (!variantId) return;

        const name = input.value;
        const optionValueInputs = variantRow.querySelector(`.nala-ws-addtocart__bulk-variant-option-value-${name}`);
        if (optionValueInputs) {
          optionValueInputs.classList.toggle("nala-hidden", !input.checked);
        }

        let price = 0;
        variantRow
          .querySelectorAll(".nala-ws-addtocart__bulk-variant-option-input:checked")
          .forEach((input) => {
            // @ts-ignore
            price += Number(input.dataset.price || 0);
          });

        const priceElement = variantRow.querySelector(".nala-ws-addtocart__bulk-variant-price-option");
        if (priceElement) {
          priceElement.innerHTML = price > 0 && input.checked ? `<span>+${this.nlFormatMoney(price, this.currencySign, false)}</span> ${this.uomAvailable ? "<span>/Unit</span>" : ""}` : "";
        }
      });
    });
  }

  /**
   * Adds the custom option change listeners
   * @returns {void}
   */
  nlSingleAddCustomOptionChangeListeners() {
    this.querySelectorAll(".nala-ws-addtocart__single-custom-option-input").forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.addEventListener("change", (event) => {
        /** @type {HTMLElement | null} */
        const variantRow = this.querySelector(".nala-ws-addtocart__single");
        if (!variantRow) return;

        const variantId = variantRow.dataset.variantId || "";
        if (!variantId) return;

        const name = input.value;
        const optionValueInputs = variantRow.querySelector(`.nala-ws-addtocart__single-custom-option-value-${name}`);
        if (optionValueInputs) {
          optionValueInputs.classList.toggle("nala-hidden", !input.checked);
        }

        let price = Number(input.dataset.price || 0);

        const priceElement = variantRow.querySelector(`.nala-ws-addtocart__single-custom-option-price-${name}`);
        if (priceElement) {
          priceElement.innerHTML = price > 0 && input.checked ? ` (+${this.nlFormatMoney(price, this.currencySign, false)}${this.uomAvailable ? "/Unit" : ""})` : "";
        }
      });
    });
  }
}

customElements.define("nala-ws-addtocart", NalaWholesaleAddToCart);
